import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Hash, MessageSquare, Calendar, ShoppingBag, LifeBuoy, Users } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { themeFor } from "@/lib/communities";
import { Button } from "@/components/ui/button";

export default function Communities() {
    const [params] = useSearchParams();
    const selected = params.get("c");
    const { data: communities } = useFetch("/communities");
    const { data: servers } = useFetch("/servers");
    const { data: mine, reload } = useFetch("/me/communities");

    if (!communities) return <Loader />;

    const join = async (c) => {
        try {
            const { data } = await api.post(`/communities/${c.key}/join`);
            reload();
            let desc = data.message;
            if (data.role_sync?.attempted) {
                const a = data.role_sync.assigned || [];
                desc = a.length ? `Discord roles assigned: ${a.join(", ")}` : data.message;
            }
            toast.success(`Joined ${c.name}`, { description: desc });
        } catch (e) {
            toast.error("Could not join", { description: e.response?.data?.detail || "Try again." });
        }
    };

    const shown = selected ? communities.filter((c) => c.key === selected) : communities;

    return (
        <div data-testid="player-communities">
            <PageHeader title="Communities" subtitle="Join a Rust community and auto-sync your Discord roles." />
            <div className="space-y-6">
                {shown.map((c) => {
                    const t = themeFor(c);
                    const srv = (servers || []).find((s) => s.community_key === c.key);
                    const joined = (mine || []).includes(c.key);
                    return (
                        <div key={c.key} className="tactical-card rounded-lg overflow-hidden" style={{ borderTop: `3px solid ${t.accent}` }}>
                            <div className="p-5" style={{ background: t.gradient }}>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <div className="label-mono" style={{ color: t.accent2 }}>{c.platform}</div>
                                        <h3 className="font-heading text-2xl font-extrabold uppercase text-foreground">{c.name}</h3>
                                        <p className="text-sm text-muted-foreground mt-1">{c.mode}{c.special_mode ? ` · ${c.special_mode}` : ""}{c.pvp_note ? ` · ${c.pvp_note}` : ""}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <StatusBadge status={(srv?.status || "online")} />
                                        {joined ? (
                                            <Button data-testid={`leave-${c.key}`} variant="outline" className="border-danger/40 text-danger hover:bg-danger/10"
                                                onClick={async () => { await api.post(`/communities/${c.key}/leave`); reload(); toast("Left community"); }}>
                                                Leave
                                            </Button>
                                        ) : (
                                            <Button data-testid={`join-${c.key}`} className="font-heading uppercase text-black" style={{ background: t.accent }} onClick={() => join(c)}>
                                                Join Community
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="label-mono text-muted-foreground mb-3">Discord Shortcuts</div>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {[
                                        { icon: Hash, label: "General" },
                                        { icon: Calendar, label: "Events" },
                                        { icon: Users, label: "LFG" },
                                        { icon: ShoppingBag, label: "Shop" },
                                        { icon: LifeBuoy, label: "Support" },
                                    ].map((s) => (
                                        <button key={s.label} data-testid={`discord-shortcut-${c.key}-${s.label}`}
                                            onClick={() => toast.info(`Open #${s.label.toLowerCase()}`, { description: "Set Discord links in Admin → Discord." })}
                                            className="flex items-center gap-2 rounded-md border border-discord/30 bg-discord/5 px-3 py-2.5 text-sm text-discord hover:bg-discord/10 transition-colors">
                                            <s.icon size={15} /> {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <Footer />
        </div>
    );
}
